import { Component, FunctionalComponent } from 'preact'
import { extractRenderPropsChildren } from '../../../libs/renderProps'
import FilterOption, { FilterOptionItem } from './FilterOption'
import { BaseFilterSelectorBuilderData, FilterSelectorProps } from './interfaces'
import SearchableFilterSelector from './SearchableFilterSelector'
import { FilterSelector } from '@insided/deprecated-control/overview/filters/FiltersMenu'

interface CommonProps {
  items: FilterOptionItem[]
  isSearchable?: boolean
  onChange?: (selectedItems: string[]) => void
  onClickOutside?: () => void
}

type BuilderData = BaseFilterSelectorBuilderData & CommonProps

type Filter = FilterSelector & CommonProps

type Props = FilterSelectorProps<string[]> & CommonProps

interface State {
  data: string[]
  isSelectAll: boolean
}

const OverviewFilterTitle: FunctionalComponent = ({ children }) => {
  return (
    <div className='overview__filters-box-filter overview__filters-box-filter-title'>
      {' '}
      {children}{' '}
    </div>
  )
}

class SimpleFilterSelector extends Component<Props, State> {
  static create = (config: BuilderData): Filter => {
    const { items, ...rest } = config
    return {
      component: this,
      type: 'filter',
      getPillDisplayValue: (data: string[]): string => {
        const firstItem = items.find((item) => String(item.value) === data[0])?.name || ''
        const otherItems = data.length > 1 ? `+${data.length - 1}` : ''

        return `${firstItem} ${otherItems}`
      },
      items,
      ...rest
    }
  }

  wrapperRef: null | HTMLElement = null

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentDidUpdate(_: Props, prevState: State) {
    if (this.props.onChange && prevState.data !== this.state.data) {
      this.props.onChange(this.state.data)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  constructor(props: Props) {
    super(props)
    const { selectedFilterItems, items } = this.props
    this.state = {
      data: selectedFilterItems || [],
      isSelectAll: selectedFilterItems?.length === items?.length
    }
  }

  onFilterOptionSelection = (payload: string[]) => {
    this.setState({ data: payload, isSelectAll: false })
  }

  onClickSelectAll = () => {
    this.setState({ isSelectAll: !this.state.isSelectAll, data: [] })
    if (this.state.isSelectAll) {
      this.setState({ data: this.props.items?.map((item) => String(item.value)) || [] })
    }
  }

  setWrapperRef = (node: HTMLElement) => {
    this.wrapperRef = node
  }

  handleClickOutside = (event: Event) => {
    const isOutside = this.wrapperRef && !this.wrapperRef.contains(event.target as Node)
    if (isOutside && this.props.onClickOutside) {
      this.props.onClickOutside()
    }
  }

  renderItem = (item: FilterOptionItem, index: number) => {
    if (item.name === 'separator') {
      return <hr className='separator' />
    }
    if (item.name === 'title') {
      return <OverviewFilterTitle>{item.value}</OverviewFilterTitle>
    }

    const { data, isSelectAll } = this.state

    return (
      <FilterOption
        key={index}
        item={item}
        data={data}
        onFilterOptionSelection={this.onFilterOptionSelection}
        isSelectAll={isSelectAll}
      />
    )
  }

  render() {
    const { name, items, isSearchable, children } = this.props
    const { data, isSelectAll } = this.state

    const selectionLabel = isSelectAll ? 'Deselect all' : 'Select all'
    const { onClickSelectAll } = this

    const renderedChildren = extractRenderPropsChildren(children)({
      data,
      isApplyButtonDisabled: data.length === 0
    })
    if (isSearchable) {
      return (
        <div ref={this.setWrapperRef}>
          <SearchableFilterSelector
            items={items}
            placeholder={`Search for ${name}`}
            onClickSelectAll={onClickSelectAll}
            selectionLabel={selectionLabel}
          >
            {({ items }) => {
              if (items === undefined) {
                return null
              }

              return items.map(this.renderItem)
            }}
          </SearchableFilterSelector>
          {renderedChildren}
        </div>
      )
    }

    return (
      <div ref={this.setWrapperRef}>
        <p className='select-all' onClick={onClickSelectAll}>
          {selectionLabel}
        </p>
        {items.map(this.renderItem)}
        {renderedChildren}
      </div>
    )
  }
}

export default SimpleFilterSelector
